import { Button, Card, Space, Table, Typography, Input, Select, Col, Row, Tooltip, Dropdown } from 'antd';
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ItemService } from '../../../Services/Manage/ItemService';
import { StockLedgerService } from '../../../Services/Manage/StockLedgerService';
import {
    BellOutlined, WarningOutlined, RiseOutlined, ApartmentOutlined, InboxOutlined,
    DownloadOutlined, SettingOutlined, ReloadOutlined, ShoppingCartOutlined, MoreOutlined, FilterOutlined,
} from '@ant-design/icons';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';


const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find(p => p.link === link) || { add: false, edit: false, delete: false, read: false, view_all: false };
    } catch { return { add: false, edit: false, delete: false, read: false, view_all: false }; }
};

const STATUS_COLORS = {
    "Out of Stock": "#f5222d",
    "Critical": "#fa8c16",
    "Low": "#faad14",
};

const StatCard = ({ title, value, sub, color, bg, icon }) => (
    <Card bodyStyle={{ padding: '10px 12px' }} style={{ borderRadius: 8, border: '1px solid #f0f0f0', height: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: 3 }}>{title}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: color, lineHeight: 1.2 }}>{value}</div>
                {sub && <div style={{ fontSize: 10, color: '#1677ff', marginTop: 3, cursor: 'pointer' }}>{sub}</div>}
            </div>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: color, flexShrink: 0 }}>
                {icon}
            </div>
        </div>
    </Card>
);

const getRowStatus = (currentStock, reorderLevel) => {
    if (currentStock <= 0) return "Out of Stock";
    if (currentStock <= reorderLevel / 2) return "Critical";
    return "Low";
};

const LowStockAlertDashboard = () => {
    const navigate = useNavigate();
    const perm = getPermission('/inventory/low-stock-alert');
    const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem("ome_user")) || {}; } catch { return {}; }
    })();
    const currentUserId = currentUser.uid;

    const [allItems, setAllItems] = useState([]);
    const [stockMap, setStockMap] = useState({});
    const [searchText, setSearchText] = useState('');
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(new Date());

    const getAllItems = async () => {
        setLoading(true);
        try {
            const request = await ItemService.getAllItem();
            setAllItems((request.data || []).filter((i) => i.is_active));
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const getStockMap = async () => {
        try {
            const request = await StockLedgerService.getOnHandMap();
            setStockMap(request.data || {});
        } catch (error) { console.error(error); }
    };


    const visibleItems = useMemo(
        () => perm.view_all ? allItems : allItems.filter((item) => String(item.added_by) === String(currentUserId)),
        [allItems, perm.view_all, currentUserId]
    );

    const refreshAll = () => {
        getAllItems();
        getStockMap();
        setLastUpdated(new Date());
    };

    useEffect(() => {
        refreshAll();
        const interval = setInterval(refreshAll, 15 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const lowStockRows = useMemo(() => {
        return visibleItems
            .map((item) => {
                const currentStock = stockMap[String(item.id)] ?? Number(item.current_stock || 0);
                const reorderLevel = Number(item.reorder_level || 0);
                if (currentStock > reorderLevel) return null;
                const maxStock = Number(item.maximum_stock || 0);
                const orderQty = maxStock > 0 ? Math.max(maxStock - currentStock, 0) : 0;
                const status = getRowStatus(currentStock, reorderLevel);
                const valueAtRisk = orderQty * Number(item.cost_price || 0);
                return { ...item, currentStock, reorderLevel, orderQty, status, valueAtRisk };
            })
            .filter(Boolean);
    }, [visibleItems, stockMap]);

    const filteredRows = useMemo(() => {
        let filtered = lowStockRows;
        if (searchText) {
            filtered = filtered.filter((r) =>
                [r.item_name, r.sku_barcode].some((v) => v?.toString().toLowerCase().includes(searchText.toLowerCase()))
            );
        }
        if (selectedWarehouse) filtered = filtered.filter((r) => r.warehouse === selectedWarehouse);
        if (selectedCategory) filtered = filtered.filter((r) => r.category === selectedCategory);
        if (selectedSupplier) filtered = filtered.filter((r) => r.supplier === selectedSupplier);
        if (selectedStatus) filtered = filtered.filter((r) => r.status === selectedStatus);
        return filtered;
    }, [lowStockRows, searchText, selectedWarehouse, selectedCategory, selectedSupplier, selectedStatus]);

    const totalLowStock = lowStockRows.length;
    const criticalCount = useMemo(() => lowStockRows.filter((r) => r.status === "Out of Stock").length, [lowStockRows]);
    const belowReorderCount = useMemo(() => lowStockRows.filter((r) => r.status !== "Out of Stock").length, [lowStockRows]);
    const warehousesAffected = useMemo(() => new Set(lowStockRows.map((r) => r.warehouse).filter(Boolean)), [lowStockRows]);
    const totalValueAtRisk = useMemo(() => lowStockRows.reduce((sum, r) => sum + r.valueAtRisk, 0), [lowStockRows]);

    const statusBreakdownData = useMemo(() => {
        const counts = { "Out of Stock": 0, "Critical": 0, "Low": 0 };
        lowStockRows.forEach((r) => { counts[r.status]++; });
        return Object.entries(counts).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value, color: STATUS_COLORS[name] }));
    }, [lowStockRows]);
    const statusBreakdownTotal = statusBreakdownData.reduce((a, b) => a + b.value, 0);

    const topItemsAtRisk = useMemo(
        () => lowStockRows.slice().sort((a, b) => b.valueAtRisk - a.valueAtRisk).slice(0, 5),
        [lowStockRows]
    );

    const warehouseBreakdown = useMemo(() => {
        const map = {};
        lowStockRows.forEach((r) => {
            const key = r.warehouse || "-";
            map[key] = (map[key] || 0) + 1;
        });
        return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
    }, [lowStockRows]);

    const warehouseOptions = [...new Set(visibleItems.map((i) => i.warehouse).filter(Boolean))].map((c) => ({ label: c, value: c }));
    const categoryOptions = [...new Set(visibleItems.map((i) => i.category).filter(Boolean))].map((c) => ({ label: c, value: c }));
    const supplierOptions = [...new Set(visibleItems.map((i) => i.supplier).filter(Boolean))].map((c) => ({ label: c, value: c }));
    const statusOptions = ["Out of Stock", "Critical", "Low"].map((s) => ({ label: s, value: s }));

    const REPORT_COLUMNS = [
        { label: "Item / Product", key: "item_name" },
        { label: "SKU / Barcode", key: "sku_barcode" },
        { label: "Category", key: "category" },
        { label: "Warehouse", key: "warehouse" },
        { label: "Current Stock", key: "currentStock" },
        { label: "Reorder Level", key: "reorderLevel" },
        { label: "Order Qty (Suggested)", key: "orderQty" },
        { label: "Status", key: "status" },
        { label: "Value at Risk (Rs.)", key: "valueAtRisk" },
        { label: "Last Updated", key: "updated_at" },
    ];

    const buildReportRows = () => filteredRows.map((r) => ({
        "Item / Product": r.item_name || "-",
        "SKU / Barcode": r.sku_barcode || "-",
        "Category": r.category || "-",
        "Warehouse": r.warehouse || "-",
        "Current Stock": r.currentStock,
        "Reorder Level": r.reorderLevel,
        "Order Qty (Suggested)": Number(r.orderQty).toFixed(0),
        "Status": r.status,
        "Value at Risk (Rs.)": r.valueAtRisk,
        "Last Updated": r.updated_at ? new Date(r.updated_at).toLocaleDateString('en-GB') : '-',
    }));

    const handleExportExcel = () => {
        const rows = buildReportRows();
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Low Stock Report");
        XLSX.writeFile(wb, `Low-Stock-Report-${dayjsStamp()}.xlsx`);
    };

    const handleExportPDF = () => {
        const rows = buildReportRows();
        const doc = new jsPDF({ orientation: "landscape" });
        doc.setFontSize(14);
        doc.text("Low Stock Alert Report", 14, 14);
        doc.setFontSize(9);
        doc.text(`Generated: ${new Date().toLocaleString('en-GB')}`, 14, 20);
        autoTable(doc, {
            startY: 26,
            head: [REPORT_COLUMNS.map((c) => c.label)],
            body: rows.map((r) => REPORT_COLUMNS.map((c) => r[c.label])),
            styles: { fontSize: 8 },
            headStyles: { fillColor: [22, 88, 180] },
        });
        doc.save(`Low-Stock-Report-${dayjsStamp()}.pdf`);
    };

    const dayjsStamp = () => new Date().toISOString().slice(0, 10);

    const exportMenuItems = [
        { key: "excel", label: "Download as Excel (.xlsx)", onClick: handleExportExcel },
        { key: "pdf", label: "Download as PDF", onClick: handleExportPDF },
    ];

    const columns = [
        { title: '#', dataIndex: 'key', width: '4%', align: 'center', key: 'key', render: (_, __, index) => index + 1 },
        {
            title: 'Item / Product', dataIndex: 'item_name', key: 'item_name',
            render: (v, record) => (
                <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{v}</div>
                    <div style={{ fontSize: 12, color: '#888' }}>{record.description}</div>
                </div>
            ),
        },
        { title: 'SKU / Barcode', dataIndex: 'sku_barcode', key: 'sku_barcode', render: (v) => v || '-' },
        { title: 'Category', dataIndex: 'category', key: 'category', render: (v) => v || '-' },
        { title: 'Warehouse', dataIndex: 'warehouse', key: 'warehouse', render: (v) => v || '-' },
        {
            title: 'Current Stock', dataIndex: 'currentStock', key: 'currentStock', align: 'right',
            render: (v, record) => (
                <Tooltip title={v <= 0 ? "Out of stock — raise a Purchase Order" : `Below reorder level (${record.reorderLevel}) — raise a Purchase Order`}>
                    <span style={{ fontWeight: 600, color: v < 0 ? '#f5222d' : v === 0 ? '#f5222d' : '#faad14', cursor: 'help' }}>
                        {v}
                    </span>
                </Tooltip>
            ),
        },
        { title: 'Reorder Level', dataIndex: 'reorderLevel', key: 'reorderLevel', align: 'right' },
        { title: 'Order Qty (Suggested)', dataIndex: 'orderQty', key: 'orderQty', align: 'right', render: (v) => v.toFixed(0) },
        {
            title: 'Status', key: 'status',
            render: (_, record) => (
                <span style={{
                    background: `${STATUS_COLORS[record.status]}1a`, color: STATUS_COLORS[record.status],
                    padding: '3px 10px', borderRadius: 6, fontWeight: 600, fontSize: 12,
                }}>{record.status}</span>
            ),
        },
        { title: 'Value at Risk (₹)', dataIndex: 'valueAtRisk', key: 'valueAtRisk', align: 'right', render: (v) => v.toLocaleString('en-IN') },
        { title: 'Last Updated', dataIndex: 'updated_at', key: 'updated_at', render: (v) => v ? new Date(v).toLocaleDateString('en-GB') : '-' },
        {
            title: 'Action', key: 'Action', fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Create Purchase Order">
                        <Button
                            icon={<ShoppingCartOutlined />}
                            size="middle"
                            onClick={() => navigate('/inventory/purchase-orders/add-purchase-order', {
                                state: { action: 'Add', enableEdit: true, from: '/inventory/low-stock-alert' }
                            })}
                        />
                    </Tooltip>

                    {/* 
                    <Dropdown menu={{ items: [{ key: '1', label: 'Ignore' }, { key: '2', label: 'View Item' }] }}>
                        <Button icon={<MoreOutlined />} size="middle" />
                    </Dropdown> */}
                </Space>
            ),
        },
    ];

    return (
        <Card bordered={false} bodyStyle={{ padding: 0 }}>
            <div style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <Typography.Title level={4} style={{ margin: 0, color: '#1658b4' }}>Low Stock Alert</Typography.Title>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>Dashboard &gt; Inventory Management &gt; Low Stock Alert</Typography.Text>
                </div>
                <Space wrap>
                    <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                        <Button size="middle" icon={<DownloadOutlined />}>Export</Button>
                    </Dropdown>                    <Button size="middle" icon={<SettingOutlined />}>Settings</Button>
                    <Button size="middle" type="primary" icon={<ReloadOutlined />} onClick={refreshAll} style={{ background: '#1677ff', borderColor: '#1677ff' }}>
                        Refresh
                    </Button>
                </Space>
            </div>

            <div style={{ padding: '8px 16px 0' }}>
                <Row gutter={[8, 8]}>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Total Low Stock Items" value={totalLowStock} sub="View all items" color="#faad14" bg="#fffbe6" icon={<BellOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Critical (Out of Stock)" value={criticalCount} sub="View critical items" color="#f5222d" bg="#fff1f0" icon={<WarningOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Below Reorder Level" value={belowReorderCount} sub="View below reorder items" color="#1677ff" bg="#f0f7ff" icon={<RiseOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                        <StatCard title="Warehouses Affected" value={warehousesAffected.size} sub="View warehouses" color="#722ed1" bg="#f5eeff" icon={<ApartmentOutlined />} />
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <StatCard title="Total Value at Risk" value={`₹ ${totalValueAtRisk.toLocaleString('en-IN')}`} sub="View details" color="#52c41a" bg="#f6ffed" icon={<InboxOutlined />} />
                    </Col>
                </Row>
            </div>

            <div style={{ padding: '6px 16px 16px' }}>
                <Row gutter={[8, 8]}>
                    <Col xs={24} lg={17}>
                        <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Row gutter={[16, 16]} align="bottom">
                                <Col xs={24} md={5}>
                                    <Input
                                        allowClear
                                        placeholder="Search item, SKU or barcode..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                </Col>
                                <Col xs={12} md={4}>
                                    <Select allowClear placeholder="All Warehouses" options={warehouseOptions}
                                        value={selectedWarehouse} onChange={setSelectedWarehouse} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={12} md={4}>
                                    <Select allowClear placeholder="All Categories" options={categoryOptions}
                                        value={selectedCategory} onChange={setSelectedCategory} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={12} md={4}>
                                    <Select allowClear placeholder="All Suppliers" options={supplierOptions}
                                        value={selectedSupplier} onChange={setSelectedSupplier} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={12} md={4}>
                                    <Select allowClear placeholder="All Status" options={statusOptions}
                                        value={selectedStatus} onChange={setSelectedStatus} style={{ width: "100%" }} />
                                </Col>
                                <Col xs={12} md={3}>
                                    <Button icon={<FilterOutlined />} style={{ width: "100%" }}>More Filters</Button>
                                </Col>
                            </Row>
                        </Card>

                        <Table
                            loading={loading}
                            scroll={{ x: 'max-content' }}
                            rowKey="id"
                            dataSource={filteredRows}
                            columns={columns}
                        />
                    </Col>

                    <Col xs={24} lg={7}>
                        <Card style={{ borderRadius: 10, marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Stock Status Breakdown</Typography.Text>
                            {statusBreakdownTotal === 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 130, color: '#bbb', fontSize: 13 }}>
                                    <span style={{ fontSize: 28 }}>✅</span>
                                    <span style={{ marginTop: 6 }}>No low stock items</span>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 8 }}>
                                    <div style={{ width: 110, height: 110, flexShrink: 0, position: 'relative' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <RechartsTooltip contentStyle={{ fontSize: 11, padding: '4px 8px', borderRadius: 6 }} formatter={(v, n) => [`${v} (${statusBreakdownTotal ? Math.round((v / statusBreakdownTotal) * 100) : 0}%)`, n]} />
                                                <Pie data={statusBreakdownData} dataKey="value" nameKey="name" innerRadius={34} outerRadius={52} paddingAngle={2}>
                                                    {statusBreakdownData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                                </Pie>
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
                                            <div style={{ fontSize: 18, fontWeight: 700, color: '#333', lineHeight: 1 }}>{statusBreakdownTotal}</div>
                                            <div style={{ fontSize: 9, color: '#888' }}>Total</div>
                                        </div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        {statusBreakdownData.map((e, i) => (
                                            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, marginBottom: 6 }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    <span style={{ color: e.color, fontSize: 14 }}>●</span>
                                                    <span style={{ color: '#333' }}>{e.name}</span>
                                                </span>
                                                <span style={{ fontWeight: 700, color: '#333' }}>{e.value} ({statusBreakdownTotal ? Math.round((e.value / statusBreakdownTotal) * 100) : 0}%)</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Card>

                        <Card style={{ borderRadius: 10, marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography.Text strong>Top Items at Risk</Typography.Text>
                            </div>
                            <div style={{ marginTop: 12 }}>
                                {topItemsAtRisk.map((r, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 10 }}>
                                        <span style={{ color: '#333' }}>{i + 1}. {r.item_name}</span>
                                        <span style={{ fontWeight: 700, color: '#1658b4' }}>₹ {r.valueAtRisk.toLocaleString('en-IN')}</span>
                                    </div>
                                ))}
                                {topItemsAtRisk.length === 0 && <Typography.Text type="secondary" style={{ fontSize: 12 }}>No items at risk</Typography.Text>}
                            </div>
                        </Card>

                        <Card style={{ borderRadius: 10, marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Warehouses Affected</Typography.Text>
                            <div style={{ marginTop: 12 }}>
                                {warehouseBreakdown.map((w, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 8 }}>
                                        <span style={{ color: '#333' }}>{w.name}</span>
                                        <span style={{ fontWeight: 700 }}>{w.value}</span>
                                    </div>
                                ))}
                                {warehouseBreakdown.length === 0 && <Typography.Text type="secondary" style={{ fontSize: 12 }}>No data</Typography.Text>}
                            </div>
                        </Card>

                        <Card style={{ borderRadius: 10 }} bodyStyle={{ padding: 12 }}>
                            <Typography.Text strong>Last Updated</Typography.Text>
                            <div style={{ marginTop: 10, fontSize: 12, color: '#666' }}>
                                {lastUpdated.toLocaleString('en-GB')}
                                <div style={{ marginTop: 4, color: '#52c41a', fontWeight: 600 }}>Auto refresh is enabled — Every 15 min</div>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Card>
    );
};

export default LowStockAlertDashboard;